import React, { useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';

import Text from '../../../shared/_TBComponents/Text';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import { ColorPrimary, ColorWhite } from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  setActiveReduxModal,
  setCurrentGroup,
  setJournalTab,
  setSelectedAthlete,
} from '../ducks/journalActions';

const TEXT_PROPS = {
  fontSize: '16px',
  color: ColorPrimary,
};

const ArrowIconContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 8px;
  padding-top: 2px;
`;

const BorderBottomDiv = styled('div')`
  border-bottom: 3px solid ${ColorPrimary};
  width: 90%;
`;

const BorderBottomEmpty = styled('div')`
  border-bottom-width: 3px;
`;

const BoxContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 100%;
`;

const InnerItems = styled('div')`
  display: flex;
`;

const TabBox = styled('button')`
  align-items: center;
  background-color: ${ColorWhite};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  width: 33%;
`;

const TabSelectorDiv = styled('div')`
  background: ${ColorWhite};
  border: ${(props) => (props.isPrimaryDarkTheme ? `1px solid ${ColorWhite}` : '')};
  border-radius: 12px;
  box-shadow: 0px 2px 4px 0px rgb(0 0 0 / 10%);
  display: flex;
  flex-direction: column;
  height: 75px;
  justify-content: space-between;
  margin-top: 10px;
  user-select: none;
`;

const TabSelector = ({ scrollToTop }) => {
  const {
    activeTab,
    currentAthlete,
    currentGroup,
  } = useSelector((state) => state.journal.data);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const dispatch = useDispatch();

  const isPrimaryDarkTheme = currentUser?.userSkin?.darkTheme;
  const groupDisplayName = currentGroup?.name || 'Groups';

  const athleteDisplayName = useMemo(() => {
    if (!currentAthlete?.first && !currentAthlete?.userName) return 'Athlete';
    if (currentAthlete.id === currentUser.id) return 'My Journal';
    if (currentAthlete?.first) return `${currentAthlete?.first[0]}. ${currentAthlete?.last}`;
    return currentAthlete?.userName;
  }, [currentAthlete, currentUser]);

  const handleAthletePickerClick = () => {
    dispatch(setActiveReduxModal('athlete-picker-modal'));
    journalTracker('Main page', 'Single athlete tab clicked - Athlete picker modal opened');
  };

  const handleGroupPickerClick = () => {
    dispatch(setActiveReduxModal('group-picker-modal'));
    journalTracker('Main page', 'Groups tab clicked - Group picker modal opened');
  };

  const handleAllAthletesClick = () => {
    batch(() => {
      if (currentGroup?.id) {
        dispatch(setCurrentGroup(null, null));
      }
      if (currentAthlete?.id) {
        dispatch(setSelectedAthlete({}));
      }
      dispatch(setJournalTab('ALL_ATHLETES'));
      scrollToTop();
    });
    journalTracker('Main page', 'All athletes tab clicked');
  };

  useEffect(() => {
    if (currentAthlete?.id && activeTab !== 'SINGLE_ATHLETE') {
      batch(() => {
        dispatch(setJournalTab('SINGLE_ATHLETE'));
        dispatch(setCurrentGroup(null, null));
      });
    }
  }, [activeTab, currentAthlete?.id, dispatch]);

  useEffect(() => {
    if (currentGroup?.id && activeTab !== 'GROUPS') {
      batch(() => {
        dispatch(setJournalTab('GROUPS'));
        dispatch(setSelectedAthlete({}));
      });
    }
  }, [activeTab, currentGroup?.id, dispatch]);

  const renderTab = (label, onClick, activeTabValue, ariaLabel, showArrow = false) => {
    const isActive = activeTab === activeTabValue;

    return (
      <TabBox
        onClick={onClick}
        aria-label={ariaLabel}
        aria-selected={isActive}
        role='tab'
      >
        <div />
        <InnerItems>
          <Text styledTextProps={TEXT_PROPS}>{label}</Text>
          {showArrow && (
            <ArrowIconContainer>
              <IcomoonReact aria-hidden='true' iconSet={iconSet} size={12} icon='down-arrow' color={ColorPrimary} />
            </ArrowIconContainer>
          )}
        </InnerItems>
        {isActive ? <BorderBottomDiv /> : <BorderBottomEmpty />}
      </TabBox>
    );
  };

  return (
    <TabSelectorDiv isPrimaryDarkTheme={isPrimaryDarkTheme} role='tablist'>
      <BoxContainer>
        {renderTab(athleteDisplayName, handleAthletePickerClick, 'SINGLE_ATHLETE', `Select athlete: ${athleteDisplayName}`, true)}
        {renderTab(groupDisplayName, handleGroupPickerClick, 'GROUPS', `Select group: ${groupDisplayName}`, true)}
        {renderTab('All Athletes', handleAllAthletesClick, 'ALL_ATHLETES', 'View all athletes', false)}
      </BoxContainer>
    </TabSelectorDiv>
  );
};

TabSelector.propTypes = {
  scrollToTop: PropTypes.func.isRequired,
};

export default TabSelector;
